import { useCallback, useMemo } from 'react';

export default function useGridColumnState({
  columns,
  defaultColumnVisibilityModel,
  gridApiRef,
  settings,
  tableKey,
  updateSettings,
}) {
  const validColumnFields = useMemo(
    () => new Set(columns.map((col) => col.field)),
    [columns]
  );

  const orderedColumns = useMemo(() => {
    const order = settings.columnOrder;
    const widths = settings.columnWidths ?? {};

    const applyWidths = (cols) =>
      cols.map((col) => {
        const persistedWidth = widths[col.field];
        if (persistedWidth != null) {
          // eslint-disable-next-line no-unused-vars
          const { flex, ...rest } = col;
          return { ...rest, width: persistedWidth };
        }
        return col;
      });

    if (!order || !Array.isArray(order) || order.length === 0) {
      return applyWidths(columns);
    }

    const byField = new Map(columns.map((col) => [col.field, col]));
    const placed = new Set(order.filter((field) => byField.has(field)));
    const result = order
      .map((field) => byField.get(field))
      .filter((col) => !!col);

    columns.forEach((col, codeIdx) => {
      if (placed.has(col.field)) return;

      let insertAt = result.length;
      for (let j = codeIdx + 1; j < columns.length; j += 1) {
        const laterField = columns[j].field;
        if (placed.has(laterField)) {
          const pos = result.findIndex((c) => c.field === laterField);
          if (pos !== -1) {
            insertAt = pos;
            break;
          }
        }
      }
      result.splice(insertAt, 0, col);
      placed.add(col.field);
    });

    return applyWidths(result);
  }, [columns, settings.columnOrder, settings.columnWidths]);

  const effectiveColumnVisibilityModel = useMemo(() => {
    const model =
      settings.columnVisibilityModel ?? defaultColumnVisibilityModel;
    if (!model) return model;

    const sanitized = {};
    for (const [field, visible] of Object.entries(model)) {
      if (validColumnFields.has(field)) {
        sanitized[field] = visible;
      }
    }
    return sanitized;
  }, [
    settings.columnVisibilityModel,
    defaultColumnVisibilityModel,
    validColumnFields,
  ]);

  const handleColumnVisibilityModelChange = useCallback(
    (newModel) => {
      updateSettings({
        columnVisibilityModel: newModel,
      });
    },
    [updateSettings]
  );

  const handleColumnWidthChange = useCallback(
    (params) => {
      updateSettings({
        columnWidths: {
          ...(settings.columnWidths ?? {}),
          [params.colDef.field]: params.width,
        },
      });
    },
    [updateSettings, settings.columnWidths]
  );

  const handleColumnOrderChange = useCallback(
    (_params) => {
      if (!tableKey) return;
      if (gridApiRef.current) {
        const orderedFields = gridApiRef.current
          .getAllColumns()
          .map((col) => col.field);
        updateSettings({ columnOrder: orderedFields });
      }
    },
    [tableKey, updateSettings, gridApiRef]
  );

  return {
    effectiveColumnVisibilityModel,
    handleColumnOrderChange,
    handleColumnVisibilityModelChange,
    handleColumnWidthChange,
    orderedColumns,
  };
}
